import React from "react";
import {
  ToggleLeftIcon,
  CalendarIcon,
  HashIcon,
  TextAaIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  PercentIcon,
} from "@phosphor-icons/react";
import { ScalarKind, EntityKind } from "../types";
import EntityIcon from "./EntityIcon";
import { entityKindMap } from "../utils";
import { PERCENT_POINTS, isDollarUnit } from "@/utils/numberlike";

interface InputTypeRendererProps {
  scalarType: ScalarKind | null;
  scalarUnit: string | null;
  entityTypes: EntityKind[] | null;
  isArray?: boolean;
}

const InputTypeRenderer: React.FC<InputTypeRendererProps> = ({
  scalarType,
  scalarUnit,
  entityTypes,
  isArray = false,
}) => {
  // Helper function to get scalar icon and label
  const getScalarInfo = (): {
    IconComponent: React.ComponentType<{ size?: number; className?: string }>;
    label: string;
  } | null => {
    if (!scalarType) return null;

    if (scalarType === "number" && isDollarUnit(scalarUnit)) {
      return { IconComponent: CurrencyDollarIcon, label: "Dollar" };
    }
    if (
      scalarType === "number" &&
      (scalarUnit === "percent" || scalarUnit === PERCENT_POINTS)
    ) {
      return { IconComponent: PercentIcon, label: "Percent" };
    }
    if (scalarType === "boolean") {
      return { IconComponent: ToggleLeftIcon, label: "Boolean" };
    }
    if (scalarType === "date") {
      return { IconComponent: CalendarIcon, label: "Date" };
    }
    if (scalarType === "number") {
      return { IconComponent: HashIcon, label: "Number" };
    }
    if (scalarType === "string") {
      return {
        IconComponent: TextAaIcon,
        label: scalarUnit === "multiline" ? "Multiline Text" : "Text",
      };
    }
    if (scalarType === "address") {
      return { IconComponent: MapPinIcon, label: "Address" };
    }
    return { IconComponent: TextAaIcon, label: "Unknown" };
  };

  const scalarInfo = getScalarInfo();
  const hasEntityTypes = entityTypes && entityTypes.length > 0;

  // If no types at all, show nothing
  if (!hasEntityTypes && !scalarInfo) {
    return null;
  }

  // Cardinality is field metadata, spoken in the kind's own line as quiet
  // ink — "Person · list" — never a parenthesized link-blue "(List)" that
  // reads as a link to a list (#11426 nooks N6).
  const listMark = isArray ? (
    <span className="text-xs text-[var(--fp-ink-3)]" data-list-designation="">
      · list
    </span>
  ) : null;

  return (
    <div className="flex flex-col gap-1">
      {/* Scalar type on top line if present */}
      {scalarInfo && (
        <div className="flex items-center gap-1">
          <scalarInfo.IconComponent size={14} className="text-gray-400" />
          <span className="text-xs text-gray-500">{scalarInfo.label}</span>
          {listMark}
        </div>
      )}

      {/* Entity types on bottom line, separated by "or" */}
      {hasEntityTypes && (
        <div className="flex items-center gap-1 flex-wrap">
          {entityTypes.map((entityKind, index) => (
            <React.Fragment key={entityKind}>
              <div className="flex items-center gap-1">
                <EntityIcon
                  kind={entityKind}
                  size={14}
                  className="text-gray-400"
                />
                <span className="text-xs text-gray-500">
                  {entityKindMap[entityKind]?.label || entityKind}
                </span>
              </div>
              {index < entityTypes.length - 1 && (
                <span className="text-xs text-gray-400">or</span>
              )}
            </React.Fragment>
          ))}
          {!scalarInfo && listMark}
        </div>
      )}
    </div>
  );
};

export default InputTypeRenderer;
